//NPM
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { omit, omitBy, isNull, isEmpty } from "lodash";

// UTILITY
import errorHandler from "Utils/error-handler";
// import { AssignDriverSchema } from './assign-driver-validation'

// HELPERS
import { toaster } from "Helpers/toaster";
import { toNum } from "Helpers/get-to-num";

//STYLE
import "../parcel-tracking-modals.scss";
import { CalendarOutlined } from "@ant-design/icons";

// HOOKS
import useSession from "Hooks/session.hook";

//COMPONENTS
import { Col, Row } from "antd";
import { AlertBox, Button, SelectInput, TextInput } from "@life-line-lab/components-react";
import ModuleTitleBar from "Components/shared/module-title-bar/module-title-bar";

interface AssignDriverBulkLooseModalProps {
  selectedOrder?: any;
  selectedOrderDetails?: any;
  type: string;
  handleCancel: any;
  onSubmit: any;
  vehicleOptions?: any;
  sortingHubOptions?: any;
  enableHandover?: boolean;
}

const AssignDriverBulkLooseModal: React.FC<AssignDriverBulkLooseModalProps> = ({
  selectedOrder,
  selectedOrderDetails,
  type,
  handleCancel,
  onSubmit,
  vehicleOptions,
  sortingHubOptions,
  enableHandover = false,
}) => {
  //NOTE: To whoever who is maintaining this code, I would like to apologize in advance. There is a lot of information in the input, but at
  //       this stage of the development, only orderID and assignedDriver is needed, but there might be chance where the vehicleID/vehicleType
  //       as well as companyType might be needed to store in the database. Do make changes accordingly if that does happen.

  const { t } = useTranslation();

  const initialRole =
    selectedOrderDetails?.assignedDriver === "THIRD_PARTY" ? "THIRDPARTY" : "COMPANY";

  const findVehicle = (profileID) => {
    for (let i = 0; i < vehicleOptions?.length; i++) {
      const vehicle = vehicleOptions[i];

      if (vehicle?.driver[0]?.profileID === profileID) {
        return vehicle?._id;
      }
    }
  };

  const initialInput = {
    orderID: selectedOrder,
    companyType: initialRole,
    vehicleID:
      initialRole === "COMPANY" ? findVehicle(selectedOrderDetails?.assignedDriverData?._id) : "",
    vehicleType: selectedOrderDetails?.assignedDriverData?.vehicleData?.vehicleType ?? "",
    thirdPartyCompany:
      initialRole === "THIRDPARTY" ? selectedOrderDetails?.assignedDriverData?.fullName : "",
    driverName: initialRole === "COMPANY" ? selectedOrderDetails?.assignedDriverData?.fullName : "",
    driverID: initialRole === "COMPANY" ? selectedOrderDetails?.assignedDriverData?._id : "",
    sortingHub: selectedOrderDetails?.midMileInfo?.sortingHub ?? "",
  };
  const initialError = {};
  const [input, setInput] = useState(initialInput);
  const [error, setError] = useState<any>(initialError);

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput({ ...input, [e.target.name]: e.target.value });
    setError({ ...error, [e.target.name]: null });
  };

  const handleSelectChange = (value: string | string[], name: string) => {
    setError({ ...error, [name]: null });

    let tempInput = { ...input, [name]: value };

    if (name === "vehicleID") {
      // autopopulate the driver id details
      const hasDriver = vehicleOptions.find((v) => v._id === value)?.driver;

      if (hasDriver) {
        tempInput = {
          ...tempInput,
          driverID: hasDriver?.profileID,
          driverName: hasDriver?.fullName,
        };
      }
    }

    setInput(tempInput);
  };

  const handleSubmit = () => {
    try {
      let valInput: any = {};

      const isRoleThirdParty = input.companyType == "THIRDPARTY" ? true : false;

      valInput = {
        orderID: input.orderID, // order id
        assignedDriver: isRoleThirdParty ? "THIRD_PARTY" : input.driverID, // if third party, this is COMPANY_TYPE.THIRD_PARTY otherwise the assignedDriver._id
        assignedDriverData: isRoleThirdParty // if third party, this is filled
          ? {
              fullName: input.thirdPartyCompany,
              vehicleData: {
                vehicleType: input.vehicleType,
              },
            }
          : null,
        handoverPointID: enableHandover ? input.sortingHub : null, // if handoverpoint is enabled, this has handover point
      };

      onSubmit(valInput);
    } catch (err) {
      console.log(err);
      const { path, value }: any = errorHandler.validation(err);
      setError({ ...error, [path]: t(`${value}`, { field: t(`label.${path}`) }) });
    }
  };

  return (
    <div className="parcel-tracking-modal">
      <ModuleTitleBar
        titleText={
          type == "ASSIGN"
            ? t("title.assignDriver").toUpperCase()
            : t("title.reassignDriver").toUpperCase()
        }
      />
      <div className="mt-20">
        <SelectInput
          showSearch
          filterOption={(inputValue, option) =>
            option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
          }
          label={t("label.selectCompanyType")}
          options={[
            { label: "DIOLKO", value: "COMPANY" },
            { label: "3rd Party", value: "THIRDPARTY" },
          ]}
          isRequired
          value={input.companyType}
          name="companyType"
          placeholder={t("txt.selectHere")}
          onChange={(value: string | string[]) => handleSelectChange(value, "companyType")}
        />
      </div>
      {input.companyType == "COMPANY" ? (
        <div>
          <div className="mt-20">
            <SelectInput
              showSearch
              label={t("label.selectVehicle")}
              options={vehicleOptions?.map((vehicle) => ({
                label: vehicle.name,
                value: vehicle._id,
              }))}
              filterOption={(inputValue, option) =>
                option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
              }
              isRequired
              value={input.vehicleID}
              name="vehicleID"
              placeholder={t("txt.selectHere")}
              onChange={(value: string | string[]) => handleSelectChange(value, "vehicleID")}
            />
            <AlertBox text={error.vehicleID} />
          </div>
          <div className="mt-10">
            <TextInput value={input.driverName} isReadonly={true} label={t("title.assignDriver")} />
          </div>
        </div>
      ) : (
        <div>
          <div className="mt-10">
            <SelectInput
              showSearch
              label={t("label.selectVehicle")}
              options={[
                { label: "CAR", value: "CAR" },
                { label: "VAN", value: "VAN" },
                { label: "BIKE", value: "BIKE" },
              ]}
              filterOption={(inputValue, option) =>
                option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
              }
              isRequired
              value={input.vehicleType}
              name="vehicleType"
              placeholder={t("txt.selectHere")}
              onChange={(value: string | string[]) => handleSelectChange(value, "vehicleType")}
            />
          </div>
          <div className="mt-10">
            <TextInput
              isRequired={true}
              label={t("label.thirdPartyName")}
              placeholder={t("txt.thirdPartyName")}
              value={input.thirdPartyCompany}
              name="thirdPartyCompany"
              onChange={handleFieldChange}
            />
          </div>
        </div>
      )}
      {enableHandover && (
        <div className="mt-10">
          <SelectInput
            showSearch
            label={t("label.selectHandoverPoint")}
            options={sortingHubOptions?.map((hub) => {
              return {
                label: hub.locationName || "",
                value: hub._id,
              };
            })}
            filterOption={(inputValue, option) =>
              option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
            }
            isRequired
            value={input.sortingHub}
            name="sortingHub"
            placeholder={t("txt.selectHere")}
            onChange={(value: string | string[]) => handleSelectChange(value, "sortingHub")}
          />
          <AlertBox text={error.sortingHub} />
        </div>
      )}

      <div className="mt-30 right">
        <Button
          type={"primary"}
          label={t("action.confirm")}
          onClick={() => {
            handleSubmit();
          }}
        />
        <Button
          className="cancel-btn"
          type={"default"}
          label={t("action.cancel")}
          onClick={() => {
            handleCancel();
          }}
        />
      </div>
    </div>
  );
};

export default AssignDriverBulkLooseModal;
